import type { NonOmsProductView, NonOmsVariantView } from './nonOmsProductDetailMapper'

/** True when a value should be shown (0 and "0" count as present). */
export const hasDisplayValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false
  if (typeof value === 'number') return !Number.isNaN(value)
  if (typeof value === 'boolean') return true
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.some((item) => hasDisplayValue(item))
  return false
}

export const hasAnyDisplayValue = (values: unknown[]): boolean =>
  values.some((value) => hasDisplayValue(value))

export const hasRichTextDisplayValue = (html: string | undefined | null): boolean => {
  if (!hasDisplayValue(html)) return false
  const text = String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return text.length > 0
}

export type ClassificationFieldVisibility = {
  category: boolean
  type: boolean
  vendor: boolean
  tags: boolean
  status: boolean
}

export const getClassificationFieldVisibility = (
  view: Pick<NonOmsProductView, 'category' | 'type' | 'vendor' | 'tags' | 'status'>
): ClassificationFieldVisibility => ({
  category: hasDisplayValue(view.category),
  type: hasDisplayValue(view.type),
  vendor: hasDisplayValue(view.vendor),
  tags: hasDisplayValue(view.tags),
  status: hasDisplayValue(view.status),
})

export const showClassificationSection = (
  fields: ClassificationFieldVisibility
): boolean => Object.values(fields).some(Boolean)

export type PricingFieldVisibility = {
  salePrice: boolean
  costPrice: boolean
  comparePrice: boolean
}

export const getPricingFieldVisibility = (
  view: Pick<NonOmsProductView, 'salePrice' | 'costPrice' | 'comparePrice'>
): PricingFieldVisibility => ({
  salePrice: hasDisplayValue(view.salePrice),
  costPrice: hasDisplayValue(view.costPrice),
  comparePrice: hasDisplayValue(view.comparePrice),
})

export const showPricingSection = (fields: PricingFieldVisibility): boolean =>
  Object.values(fields).some(Boolean)

export type ShippingFieldVisibility = {
  weight: boolean
  length: boolean
  width: boolean
  height: boolean
}

export const getShippingFieldVisibility = (
  view: Pick<NonOmsProductView, 'weight' | 'length' | 'width' | 'height'>
): ShippingFieldVisibility => ({
  weight: hasDisplayValue(view.weight),
  length: hasDisplayValue(view.length),
  width: hasDisplayValue(view.width),
  height: hasDisplayValue(view.height),
})

export const showShippingSection = (fields: ShippingFieldVisibility): boolean =>
  Object.values(fields).some(Boolean)

export const showDimensionUnitField = (fields: ShippingFieldVisibility): boolean =>
  fields.length || fields.width || fields.height

export type VariantMatrixColumnVisibility = {
  media: boolean
  salePrice: boolean
  costPrice: boolean
  available: boolean
  sku: boolean
  barcode: boolean
}

export const getVariantMatrixColumnVisibility = (
  variants: NonOmsVariantView[]
): VariantMatrixColumnVisibility => ({
  media: variants.some((variant) => hasDisplayValue(variant.imageUrl)),
  salePrice: variants.some((variant) => hasDisplayValue(variant.salePrice)),
  costPrice: variants.some((variant) => hasDisplayValue(variant.costPrice)),
  available: variants.some((variant) => hasDisplayValue(variant.available)),
  sku: variants.some((variant) => hasDisplayValue(variant.sku)),
  barcode: variants.some((variant) => hasDisplayValue(variant.barcode)),
})

export const countVariantMatrixColumns = (columns: VariantMatrixColumnVisibility): number =>
  1 +
  (columns.media ? 1 : 0) +
  (columns.salePrice ? 1 : 0) +
  (columns.costPrice ? 1 : 0) +
  (columns.available ? 1 : 0) +
  (columns.sku ? 1 : 0) +
  (columns.barcode ? 1 : 0)

/** Main/source store product detail route (not a connected channel listing). */
export const isMainStoreProductDetailView = (
  platformId: string | undefined,
  mainStoreId: number | undefined | null
): boolean => {
  if (!platformId || mainStoreId == null) return false
  return Number(platformId) === Number(mainStoreId)
}

/** Inventory Details card data gate: show while loading; hide after load when API returned no rows. */
export const showSecondaryInventorySection = (
  inventoryLoading: boolean,
  inventoryLocationsCount: number
): boolean => inventoryLoading || inventoryLocationsCount > 0

export const showInventoryDetailsSection = (
  platformId: string | undefined,
  mainStoreId: number | undefined | null,
  inventoryLoading: boolean,
  inventoryLocationsCount: number
): boolean =>
  isMainStoreProductDetailView(platformId, mainStoreId) &&
  showSecondaryInventorySection(inventoryLoading, inventoryLocationsCount)

export const showProductMediaSection = (images: string[]): boolean => hasDisplayValue(images)

export const showVariantsAndOptionsSection = (
  view: Pick<NonOmsProductView, 'hasVariants' | 'options' | 'variants'>
): boolean =>
  view.hasVariants && (view.options.length > 0 || view.variants.length > 0)

/** Product detail logs tabs: hide when API reports no records (0 total and empty list). */
export const logsApiResultHasRecords = (
  totalItems: unknown,
  records: unknown
): boolean => {
  const total = Number(totalItems ?? 0)
  if (!Number.isNaN(total) && total > 0) return true
  return Array.isArray(records) && records.length > 0
}
